import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DateTime } from 'luxon';
import { DemandeType } from '@models/enums';
import { PagedResultDTO } from '@models/shared';
import { DemandeValidation, FilteredResults, ListePoste } from '@models/api/responses';
import { FilterParams, FilterGenerique } from '@models/api/requests';
import { HttpParamsUtils } from '../../shared/utils/http-params-utils';

interface DemandeWorkflowPayload {
  id: string;
  typeDemande: DemandeType;
  motif: string;
}

@Injectable({
  providedIn: 'root'
})
export class ValidationDemandesService {
  private readonly apiUrl = environment.webService.server + environment.webService.url + environment.webService.api;

  constructor(private readonly http: HttpClient) {}

  getDemandesAValider(page: number = 0, size: number = 10, filters?: FilterParams): Observable<PagedResultDTO<DemandeValidation>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    params = HttpParamsUtils.addTypedFilterParams(params, filters);
    return this.http.get<PagedResultDTO<DemandeValidation>>(`${this.apiUrl}/demandes/a-valider`, { params });
  }

  getDemandesValidees(page: number = 0, size: number = 10, filters?: FilterParams): Observable<PagedResultDTO<DemandeValidation>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    params = HttpParamsUtils.addTypedFilterParams(params, filters);
    return this.http.get<PagedResultDTO<DemandeValidation>>(`${this.apiUrl}/demandes/validees`, { params });
  }

  getDemandesRefusees(page: number = 0, size: number = 10, filters?: FilterParams): Observable<PagedResultDTO<DemandeValidation>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    params = HttpParamsUtils.addTypedFilterParams(params, filters);
    return this.http.get<PagedResultDTO<DemandeValidation>>(`${this.apiUrl}/demandes/refusees`, { params });
  }

  getAllDemandes(page: number = 0, size: number = 10, filters?: FilterParams): Observable<PagedResultDTO<DemandeValidation>> {
    let params = new HttpParams().set('page', page.toString()).set('size', size.toString());
    params = HttpParamsUtils.addTypedFilterParams(params, filters);
    return this.http.get<PagedResultDTO<DemandeValidation>>(`${this.apiUrl}/demandes`, { params });
  }

  validerDemande(id: string, typeDemande: DemandeType, motif: string): Observable<any> {
    return this.validerDemandes([{ id, typeDemande, motif }]);
  }

  refuserDemande(id: string, typeDemande: DemandeType, motif: string): Observable<any> {
    return this.refuserDemandes([{ id, typeDemande, motif }]);
  }

  validerDemandes(demandes: DemandeWorkflowPayload[]): Observable<any> {
    const body = this.mapDemandesByType(demandes);
    return this.http.post(`${this.apiUrl}/demandes/valider`, body);
  }

  refuserDemandes(demandes: DemandeWorkflowPayload[]): Observable<any> {
    const body = this.mapDemandesByType(demandes);
    return this.http.post(`${this.apiUrl}/demandes/refuser`, body);
  }

  rappelerDemandes(demandes: DemandeWorkflowPayload[]): Observable<any> {
    const body = this.mapDemandesByType(demandes);
    return this.http.post(`${this.apiUrl}/demandes/rappeler`, body);
  }

  rappelerDemande(id: string, typeDemande: DemandeType, motif: string): Observable<any> {
    return this.rappelerDemandes([{ id, typeDemande, motif }]);
  }

  private mapDemandesByType(demandes: DemandeWorkflowPayload[]) {
    return demandes.reduce<Record<string, Record<string, string>[]>>((acc, demande) => {
      if (!acc[demande.typeDemande]) {
        acc[demande.typeDemande] = [];
      }
      acc[demande.typeDemande].push({ [demande.id]: demande.motif });
      return acc;
    }, {});
  }
}
